// src/components/staff-time/StaffManagement.jsx
import React, { useState, useEffect } from 'react';
import { Users, Plus, Edit, Trash2, Save, X } from 'lucide-react';
import { getAllStaff, getAllStaffIncludingPaused, addStaffMember, updateStaffMember, deactivateStaff } from '../../services/staffService';
import { STAFF_ROLES } from '../../utils/constants';
import { styles } from '../../styles/theme';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';

const StaffManagement = ({ onBack }) => {
  const [staff, setStaff] = useState([]);
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showPaused, setShowPaused] = useState(true);
  const [tempDeactivateModal, setTempDeactivateModal] = useState(null);
  const [tempUntil, setTempUntil] = useState('');
  const [tempReason, setTempReason] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    pin: '',
    role: STAFF_ROLES.INSTRUCTOR,
    email: '',
    phone: '',
    allowedLocations: ['center']
  });

  useEffect(() => {
    loadStaff();
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'locations'));
      const list = snapshot.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(l => l.active)
        .sort((a, b) => a.name.localeCompare(b.name));
      setLocations(list);
    } catch (err) {
      console.error('Error loading locations:', err);
    }
  };

  const loadStaff = async () => {
    setIsLoading(true);
    try {
      const staffList = await getAllStaffIncludingPaused();
      setStaff(staffList);
    } catch (error) {
      console.error('Error loading staff:', error);
      alert('Failed to load staff members');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();

    if (!formData.firstName || !formData.lastName || !formData.pin) {
      alert('Please fill in all required fields');
      return;
    }

    if (formData.pin.length !== 4 || !/^\d{4}$/.test(formData.pin)) {
      alert('PIN must be exactly 4 digits');
      return;
    }

    // Check if PIN already exists
    if (staff.some(s => s.pin === formData.pin)) {
      alert('This PIN is already in use. Please choose a different one.');
      return;
    }

    setIsLoading(true);
    try {
      await addStaffMember(formData);
      alert('✅ Staff member added successfully!');
      setShowAddForm(false);
      setFormData({
        firstName: '',
        lastName: '',
        pin: '',
        role: STAFF_ROLES.INSTRUCTOR,
        email: '',
        phone: '',
        allowedLocations: ['center']
      });
      await loadStaff();
    } catch (error) {
      console.error('Error adding staff:', error);
      alert('Failed to add staff member');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditStaff = async (e) => {
    e.preventDefault();

    if (!editingStaff) return;

    setIsLoading(true);
    try {
      await updateStaffMember(editingStaff.id, formData);
      alert('✅ Staff member updated successfully!');
      setEditingStaff(null);
      setFormData({
        firstName: '',
        lastName: '',
        pin: '',
        role: STAFF_ROLES.INSTRUCTOR,
        email: '',
        phone: '',
        allowedLocations: ['center']
      });
      await loadStaff();
    } catch (error) {
      console.error('Error updating staff:', error);
      alert('Failed to update staff member');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeactivateStaff = async (staffId, staffName) => {
    const confirmed = window.confirm(
      `Deactivate ${staffName}?\n\nThey will no longer be able to check in/out.`
    );

    if (!confirmed) return;

    setIsLoading(true);
    try {
      await deactivateStaff(staffId);
      alert('✅ Staff member deactivated');
      await loadStaff();
    } catch (error) {
      console.error('Error deactivating staff:', error);
      alert('Failed to deactivate staff member');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTempDeactivate = async () => {
    if (!tempDeactivateModal) return;
    setIsLoading(true);
    try {
      await updateStaffMember(tempDeactivateModal.id, {
        active: false,
        temporaryDeactivation: true,
        deactivatedUntil: tempUntil || null,
        deactivationReason: tempReason || null,
        deactivatedAt: new Date().toISOString(),
      });
      alert(`✅ ${tempDeactivateModal.name} temporarily deactivated${tempUntil ? ` until ${new Date(tempUntil).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}` : ''}`);
      setTempDeactivateModal(null);
      setTempUntil('');
      setTempReason('');
      await loadStaff();
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to deactivate staff member');
    } finally {
      setIsLoading(false);
    }
  };

  const handleReactivate = async (staffId, staffName) => {
    if (!window.confirm(`Reactivate ${staffName}?`)) return;
    setIsLoading(true);
    try {
      await updateStaffMember(staffId, {
        active: true,
        temporaryDeactivation: false,
        deactivatedUntil: null,
        deactivationReason: null,
      });
      alert(`✅ ${staffName} reactivated`);
      await loadStaff();
    } catch (err) {
      alert('Failed to reactivate');
    } finally {
      setIsLoading(false);
    }
  };

  const startEdit = (staffMember) => {
    setEditingStaff(staffMember);
    setFormData({
      firstName: staffMember.firstName,
      lastName: staffMember.lastName,
      pin: staffMember.pin,
      role: staffMember.role,
      email: staffMember.email || '',
      phone: staffMember.phone || '',
      allowedLocations: staffMember.allowedLocations || ['center']
    });
    setShowAddForm(true);
  };

  const cancelEdit = () => {
    setEditingStaff(null);
    setShowAddForm(false);
    setFormData({
      firstName: '',
      lastName: '',
      pin: '',
      role: STAFF_ROLES.INSTRUCTOR,
      email: '',
      phone: '',
      allowedLocations: ['center']
    });
  };

  return (
    <>
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
        padding: '2rem 0',
        marginBottom: '2rem',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem' }}>
          <button
            style={{
              background: 'rgba(255,255,255,0.2)',
              border: '2px solid white',
              color: 'white',
              padding: '0.5rem 1rem',
              borderRadius: '8px',
              fontSize: '0.875rem',
              fontWeight: 'bold',
              cursor: 'pointer',
              marginBottom: '1rem'
            }}
            onClick={onBack}
          >
            ← Back
          </button>
          <h1 style={{ fontSize: '2rem', fontWeight: 'bold', color: 'white', margin: 0 }}>
            👥 Staff Management
          </h1>
          <p style={{ color: 'rgba(255,255,255,0.9)', margin: '0.5rem 0 0 0' }}>
            Manage staff members and their check-in access
          </p>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 1rem 2rem 1rem' }}>
        {/* Add Button */}
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            style={{
              ...styles.button,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              marginBottom: '1.5rem',
              backgroundColor: '#10b981'
            }}
          >
            <Plus style={{ width: '18px', height: '18px' }} />
            Add Staff Member
          </button>
        )}

        {/* Add/Edit Form */}
        {showAddForm && (
          <div style={{ ...styles.card, marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '1rem' }}>
              {editingStaff ? 'Edit Staff Member' : 'Add New Staff Member'}
            </h2>

            <form onSubmit={editingStaff ? handleEditStaff : handleAddStaff}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
                <div>
                  <label style={styles.label}>First Name *</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                    style={styles.input}
                    required
                  />
                </div>

                <div>
                  <label style={styles.label}>Last Name *</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                    style={styles.input}
                    required
                  />
                </div>

                <div>
                  <label style={styles.label}>4-Digit PIN *</label>
                  <input
                    type="tel"
                    maxLength="4"
                    value={formData.pin}
                    onChange={(e) => {
                      if (/^\d*$/.test(e.target.value)) {
                        setFormData({ ...formData, pin: e.target.value });
                      }
                    }}
                    style={styles.input}
                    placeholder="1234"
                    required
                  />
                </div>

                <div>
                  <label style={styles.label}>Role *</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    style={styles.input}
                  >
                    <option value={STAFF_ROLES.INSTRUCTOR}>Instructor</option>
                    <option value={STAFF_ROLES.DRIVER}>Driver</option>
                    <option value={STAFF_ROLES.ADMIN_STAFF}>Admin Staff</option>
                    <option value={STAFF_ROLES.OPERATIONS}>Operations</option>
                    <option value={STAFF_ROLES.MANAGER}>Manager/Director</option>
                  </select>
                </div>

                <div>
                  <label style={styles.label}>Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    style={styles.input}
                  />
                </div>

                <div>
                  <label style={styles.label}>Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    style={styles.input}
                  />
                </div>
              </div>

              <div style={{ marginTop: '1rem' }}>
                <label style={styles.label}>Allowed Locations *</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '0.5rem' }}>
                  {locations.map(loc => (
                    <label key={loc.id} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.allowedLocations.includes(loc.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFormData({ ...formData, allowedLocations: [...formData.allowedLocations, loc.id] });
                          } else {
                            setFormData({ ...formData, allowedLocations: formData.allowedLocations.filter(l => l !== loc.id) });
                          }
                        }}
                      />
                      <span style={{ fontSize: '0.875rem' }}>{loc.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                <button
                  type="button"
                  onClick={cancelEdit}
                  style={{
                    ...styles.button,
                    flex: 1,
                    backgroundColor: '#6b7280',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <X style={{ width: '18px', height: '18px' }} />
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    ...styles.button,
                    flex: 1,
                    backgroundColor: '#10b981',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    opacity: isLoading ? 0.6 : 1
                  }}
                >
                  <Save style={{ width: '18px', height: '18px' }} />
                  {editingStaff ? 'Update' : 'Add'} Staff
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Staff List */}
        <div style={styles.card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 'bold', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Users style={{ width: '20px', height: '20px' }} />
              All Staff Members ({staff.length})
            </h2>
            <button
              onClick={() => setShowPaused(p => !p)}
              style={{ fontSize: '0.72rem', fontWeight: '600', padding: '0.25rem 0.7rem', borderRadius: '5px', border: '1px solid #e5e7eb', backgroundColor: showPaused ? '#fef3c7' : '#f3f4f6', color: showPaused ? '#92400e' : '#6b7280', cursor: 'pointer' }}
            >
              {showPaused ? '⏸ Hiding Paused Staff' : '⏸ Show Paused Staff'}
            </button>
          </div>

          {isLoading ? (
            <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem 0' }}>Loading...</p>
          ) : staff.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#6b7280', padding: '2rem 0' }}>No staff members yet. Add your first staff member above!</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Name</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>PIN</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Role</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Contact</th>
                    <th style={{ padding: '1rem', textAlign: 'left', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Locations</th>
                    <th style={{ padding: '1rem', textAlign: 'center', fontSize: '0.875rem', fontWeight: '600', color: '#374151' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {staff.filter(member => {
                    if (!showPaused && member.active === false && member.temporaryDeactivation) return false;
                    return true;
                  }).map((member, index) => (
                    <tr key={member.id} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: index % 2 === 0 ? 'white' : '#f9fafb' }}>
                      <td style={{ padding: '1rem' }}>
                        <div style={{ fontWeight: '500', fontSize: '0.875rem' }}>
                          {member.firstName} {member.lastName}
                          {member.active === false && member.temporaryDeactivation && (
                            <span style={{ marginLeft: '0.5rem', fontSize: '0.65rem', fontWeight: '700', backgroundColor: '#fef3c7', color: '#92400e', padding: '0.1rem 0.4rem', borderRadius: '3px' }}>
                              ⏸ PAUSED{member.deactivatedUntil ? ` until ${new Date(member.deactivatedUntil).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}` : ''}
                            </span>
                          )}
                        </div>
                        {member.deactivationReason && (
                          <div style={{ fontSize: '0.72rem', color: '#9ca3af', marginTop: '0.1rem' }}>{member.deactivationReason}</div>
                        )}
                      </td>
                      <td style={{ padding: '1rem', fontSize: '0.875rem', fontFamily: 'monospace', color: '#6b7280' }}>
                        {member.pin}
                      </td>
                      <td style={{ padding: '1rem', fontSize: '0.875rem', color: '#6b7280', textTransform: 'capitalize' }}>
                        {member.role.replace('_', ' ')}
                      </td>
                      <td style={{ padding: '1rem', fontSize: '0.875rem' }}>
                        <div style={{ color: '#6b7280' }}>{member.phone || '-'}</div>
                        <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>{member.email || '-'}</div>
                      </td>
                      <td style={{ padding: '1rem', fontSize: '0.75rem', color: '#6b7280' }}>
                        {member.allowedLocations?.map(locId => {
                          const found = locations.find(l => l.id === locId);
                          return found?.name || locId;
                        }).join(', ')}
                      </td>
                      <td style={{ padding: '1rem', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                          <button
                            onClick={() => startEdit(member)}
                            style={{
                              padding: '0.5rem',
                              backgroundColor: '#3b82f6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            title="Edit"
                          >
                            <Edit style={{ width: '14px', height: '14px' }} />
                          </button>
                          {member.active === false ? (
                            <button
                              onClick={() => handleReactivate(member.id, `${member.firstName} ${member.lastName}`)}
                              style={{ padding: '0.4rem 0.75rem', backgroundColor: '#059669', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.72rem', fontWeight: '700' }}
                              title="Reactivate"
                            >
                              ✅ Reactivate
                            </button>
                          ) : (
                            <button
                              onClick={() => { setTempDeactivateModal({ id: member.id, name: `${member.firstName} ${member.lastName}` }); setTempUntil(''); setTempReason(''); }}
                              style={{ padding: '0.4rem 0.75rem', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.72rem', fontWeight: '700' }}
                              title="Temporarily Deactivate"
                            >
                              ⏸ Pause
                            </button>
                          )}
                          {member.active !== false && (
                            <button
                              onClick={() => handleDeactivateStaff(member.id, `${member.firstName} ${member.lastName}`)}
                              style={{ padding: '0.5rem', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                              title="Permanently Deactivate"
                            >
                              <Trash2 style={{ width: '14px', height: '14px' }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Temp Deactivate Modal */}
      {tempDeactivateModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '1.5rem', width: '100%', maxWidth: '400px' }}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: '700', color: '#1f2937' }}>⏸ Pause {tempDeactivateModal.name}</h3>
            <p style={{ margin: '0 0 1.25rem', fontSize: '0.85rem', color: '#6b7280' }}>
              They won't appear in scheduling or check-in, but all historical data and payroll records are preserved.
            </p>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.35rem' }}>Reactivate on (optional)</label>
              <input
                type="date"
                value={tempUntil}
                onChange={e => setTempUntil(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1.5px solid #d1d5db', borderRadius: '7px', fontSize: '0.875rem', boxSizing: 'border-box' }}
              />
              <p style={{ margin: '0.3rem 0 0', fontSize: '0.72rem', color: '#9ca3af' }}>Leave blank for indefinite pause</p>
            </div>
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', color: '#374151', marginBottom: '0.35rem' }}>Reason (optional)</label>
              <input
                type="text"
                value={tempReason}
                onChange={e => setTempReason(e.target.value)}
                placeholder="e.g. Maternity leave, Summer break..."
                style={{ width: '100%', padding: '0.5rem 0.75rem', border: '1.5px solid #d1d5db', borderRadius: '7px', fontSize: '0.875rem', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => setTempDeactivateModal(null)}
                style={{ flex: 1, padding: '0.65rem', backgroundColor: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', fontWeight: '600', cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={handleTempDeactivate}
                disabled={isLoading}
                style={{ flex: 1, padding: '0.65rem', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '700', cursor: isLoading ? 'not-allowed' : 'pointer' }}
              >
                {isLoading ? 'Saving...' : '⏸ Pause Staff'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default StaffManagement;